import { ILocationKcfInformationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInlineEditor } from 'components/inline_edit/TextInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { validateNetSuiteId } from 'pages/Settings/shared/shared';
import { updateLocationKcfInformation } from 'utils/location';

export type LocationNetSuiteIdInlineEditorProps = Readonly<{
    /**
     * The location.
     */
    locationId: string;
    /**
     * The KCF-only information associated with the location
     */
    locationKcfInfo: ILocationKcfInformationResponse;
    /**
     * The callback for when data changes.
     */
    onChange: () => void;
}>;

export function LocationNetSuiteIdInlineEditor({
    locationId,
    locationKcfInfo,
    onChange,
}: LocationNetSuiteIdInlineEditorProps) {
    const {
        canViewKcfInfo: canViewNetSuite,
        canEditKcfInfo: canEditNetSuite,
    } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updateNetSuiteId, isPending } = useMutation({
        meta: { name: 'update-location-kcf-information' },
        mutationFn: (netSuiteId: string | undefined) =>
            updateLocationKcfInformation(locationId, locationKcfInfo, {
                NetSuiteId: netSuiteId || undefined,
            }),

        onSuccess: () => {
            onChange();
            showToast({
                type: 'success',
                content: 'Location NetSuite ID saved',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location NetSuite ID',
            }),
    });

    if (!canViewNetSuite) {
        return <></>;
    }

    return (
        <TextInlineEditor
            value={locationKcfInfo.NetSuiteId}
            isDisabled={isPending || !canEditNetSuite}
            onConfirm={(netSuiteId) => updateNetSuiteId(netSuiteId)}
            validate={(netSuiteId) => {
                if (netSuiteId) {
                    return validateNetSuiteId(netSuiteId);
                }
                return undefined;
            }}
            renderValue={(netSuiteId) => <code>{netSuiteId}</code>}
        />
    );
}
